import { APP_BASE_HREF } from '@angular/common';
import type { Provider } from '@angular/core';

export interface PluginAppBaseHrefOptions {
  /**
   * Route path patterns to strip from the end of the browser pathname when computing
   * {@link APP_BASE_HREF} (deep links into the plugin).
   *
   * Supports Angular-style param segments (`:id`) and multi-segment paths.
   * Longer matches win first. Example:
   * - `['lieferschein', 'lieferschein/:documentId']` so
   *   `/mount/lieferschein/abc` and `/mount/lieferschein` both yield base `/mount/`.
   */
  stripTrailingSegments?: string[];
}

function pathToParts(path: string): string[] {
  return path === '/' ? [] : path.split('/').filter(Boolean);
}

function partsToPath(parts: string[]): string {
  return parts.length ? `/${parts.join('/')}` : '/';
}

function matchesPattern(pathParts: string[], patternParts: string[]): boolean {
  if (pathParts.length < patternParts.length) {
    return false;
  }
  const suffix = pathParts.slice(-patternParts.length);
  return patternParts.every((pattern, i) => pattern.startsWith(':') || pattern === suffix[i]);
}

/**
 * Base URL for {@link PathLocationStrategy} when the plugin runs under a host mount path
 * (e.g. `/customElements/st`). Without this, inner routes resolve against `/` and fight the host router.
 */
export function pluginAppBaseHref(options?: PluginAppBaseHrefOptions): string {
  let path = window.location.pathname.replace(/\/$/, '') || '/';
  const patterns = (options?.stripTrailingSegments ?? [])
    .filter((segment): segment is string => !!segment)
    .map((segment) => segment.split('/').filter(Boolean))
    .filter((parts) => parts.length > 0)
    // Prefer longer patterns so `lieferschein/:id` wins over `lieferschein`.
    .sort((a, b) => b.length - a.length);

  let changed = true;
  while (changed) {
    changed = false;
    const pathParts = pathToParts(path);
    for (const patternParts of patterns) {
      if (!matchesPattern(pathParts, patternParts)) {
        continue;
      }
      path = partsToPath(pathParts.slice(0, -patternParts.length));
      changed = true;
      break;
    }
  }

  if (path !== '/' && !path.endsWith('/')) {
    return `${path}/`;
  }
  return path;
}

export function providePluginAppBaseHref(options?: PluginAppBaseHrefOptions): Provider {
  return {
    provide: APP_BASE_HREF,
    useFactory: () => pluginAppBaseHref(options),
  };
}
